<template>
  <div class="w-100 p-2 mt-2">
    <page-header :name="'Platform wallet'">
      <b-icon-person-fill class="mr-2"></b-icon-person-fill> {{ $t('page.title.administrator_list') }}
      <template v-slot:right>
        <router-link class="btn btn-success btn-sm"
                     :to="{ name: 'administrator-profile', params: { type: 'createAdministrator' }}">{{ $t('btn.create') }}</router-link>
      </template>
    </page-header>
    <page-card>
      <template v-slot:header>
        {{ $t('title.filters') }}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  filter-name="email"
                  :label="$t('filters.label.email')"
                  :placeholder="$t('filters.placeholder.email')">
              </filter-input>
            </div>

            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.boolean"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="enabled"
                  @change="hej($event)"
                  :label="$t('filters.label.enabled')"
                  :placeholder="$t('filters.placeholder.enabled')">
              </filter-select>
            </div>

            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.boolean"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="locked"
                  :label="$t('filters.label.locked')"
                  :placeholder="$t('filters.placeholder.locked')">
              </filter-select>
            </div>
          </div>
          <div class="row">
            <div class="col-4">
              <filter-input
                  @filterValueChanged="setTenantQueryString($event)"
                  :filter-query="filterQuery"
                  filter-name="tenantId"
                  :label="$t('filters.label.tenant')"
                  :placeholder="$t('filters.placeholder.tenant')">
              </filter-input>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <b-button @click="resetFilters()" variant="secondary">{{$t('filters.btn.clear')}}</b-button>
        <b-button @click="applyFilters()" variant="primary">{{$t('filters.btn.apply')}}</b-button>
      </template>
    </page-card>
    <page-card>
      <template v-slot:header>
        {{$t('title.list')}}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>

          <div class="table-responsive">
            <b-table
                :busy="isBusy ||  tenants.length === 0"
                striped show-empty
                :empty-text="$t('table.no_results')"
                hover
                head-variant="dark"
                outlined
                :sort-by.sync="orderColumn"
                :sort-desc.sync="orderDirection"
                :no-local-sorting="true"
                :items="items"
                :fields="fields">
              <template #cell(enabled)="data">
                <div>
                  <b-badge pill :variant="data.item.enabled ? 'success' : 'danger'">{{ data.item.enabled }}</b-badge>
                </div>
              </template>

              <template #cell(tenantId)="data">
                <div>
                  {{ tenants.filter(tenant => tenant.id.uuid === data.item.tenantId).length ? tenants.filter(tenant => tenant.id.uuid === data.item.tenantId)[0].name : ''}}
                </div>
              </template>

              <template #cell(locked)="data">
                <div>
                  <b-badge pill :variant="data.item.locked ? 'success' : 'danger'">{{ data.item.locked }}</b-badge>
                </div>
              </template>

              <template #cell(passwordChangedAt)="data">
                <div>
                  {{ data.value | dateFormat('YYYY-MM-DD HH:mm:ss') }}
                </div>
              </template>

              <template #cell(lastLogin)="data">
                <div>
                  {{ data.value | dateFormat('YYYY-MM-DD HH:mm:ss') }}
                </div>
              </template>

              <template #cell(actions)="data">
                <div class="table-action--cell">
                  <router-link class="w-100 btn btn-primary btn-sm"
                               :to="{ name: 'administrator-profile', params: { id: data.item.id }}"> {{$t('table.btn.details')}}
                  </router-link>
                </div>
              </template>

              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>{{$t('state.loading')}}</strong>
                </div>
              </template>
            </b-table>
          </div>
        </div>
      </template>
      <grid-pagination :total-count="totalCount"
                       :per-page="perPage"
                       :current-page="currentPage"
                       @load-items="paginationReload"></grid-pagination>
    </page-card>
  </div>
</template>

<script>
import { mapState } from "vuex";
import FilterSelect from "../../../components/grid/filters/FilterSelect";
import FilterInput from "../../../components/grid/filters/FilterInput";
import GridPagination from "../../../components/grid/grid-pagination/GridPagination";
import PageHeader from "../../../components/page/PageHeader";
import PageCard from "../../../components/page/PageCard";

export default {
  props: ['apiKey'],
  components: {
    GridPagination,
    FilterSelect,
    FilterInput,
    PageHeader,
    PageCard
  },
  data() {
    return {
      perPage: 10,
      currentPage: 1,
      orderColumn: 'email',
      orderDirection: true,
      filterQuery: [],
      tenantQueryString: '',
      filterOptions: {
        boolean: [
          {value: 'true', text: 'true'},
          {value: 'false', text: 'false'}
        ],
      },
      fields: [
        {
          key: 'email',
          label: this.$t('table.header.email'),
          sortable: true,
        },
        {
          key: 'enabled',
          label: this.$t('table.header.enable'),
          sortable: true
        },
        {
          key: 'locked',
          label: this.$t('table.header.locked'),
          sortable: true
        },
        {
          key: 'tenantId',
          label: this.$t('table.header.tenant'),
          sortable: true
        },
        {
          key: 'passwordChangedAt',
          label: this.$t('table.header.last_password_changed'),
          sortable: true
        },
        {
          key: 'lastLogin',
          label: this.$t('table.header.last_login_at'),
          sortable: true
        },
        {key: 'actions', label: this.$t('table.header.actions'),  sortable: false}
      ]
    };
  },
  mounted() {
    if (this.apiKey) {
      this.$store.commit('root/setApiKey', this.apiKey);
    }

    this.loadTenants();

    this.loadItems();

    document.addEventListener('keydown', this.bindEnter);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.bindEnter);

    this.$store.commit('administrator/resetAdministratorDataForm');
    this.$store.commit('administrator/resetAdministratorPasswordForm');
  },
  computed: mapState({
    isBusy: state => state.administrator.isBusy,
    items: state => state.administrator.administrators,
    totalCount: state => state.administrator.totalCount,
    tenants: state => state.administrator.tenants
  }),
  methods: {
    setTenantQueryString(e) {
      this.tenants.forEach(tenant => {
        if(tenant.name.includes(e)) {
          this.tenantQueryString = `&filters[tenantId][0][value]=${tenant.id.uuid}&filters[tenantId][0][operator]=eq`
        }
      })
    },
    bindEnter(e) {
      if (e.keyCode === 13) {
        this.applyFilters();
      }
    },
    loadItems() {
      this.$store.dispatch('administrator/getAdministrators', {
        params: {
          offset: this.perPage * (this.currentPage - 1),
          limit: this.perPage
        },
        filters: this.filterQuery,
        sort: this.createSortQuery()
      })
      .then(() => {

      });
    },
    loadTenants() {
      this.$store.dispatch('administrator/getTenants');
    },
    applyFilters: function () {
      if(this.tenantQueryString) {
        this.filterQuery.tenantId = this.tenantQueryString;
      }

      this.currentPage = 1;
      this.loadItems();
    },
    createSortQuery() {
      let sorting;
      if (this.orderColumn) {
        if (this.orderDirection === true) {
          sorting = encodeURIComponent('+' + this.orderColumn);
        } else {
          sorting = encodeURIComponent('-' + this.orderColumn);
        }
        return '&sort[]=' + sorting;
      }
      return '';
    },
    resetFilters() {
      this.filterQuery = [];
      this.$root.$emit('resetFilters');
      // this.initFilters();
      this.applyFilters();
    },
    // initFilters() {
    //   this.$store.commit('root/initFilters', {
    //     storeName: 'players',
    //     filterNames: ['phrase', 'phone', 'depositCount', 'status', 'quizCoinAmount', 'freeCoinAmount', 'lastLogin', 'created_at']
    //   });
    // },
    showAdministratorDetails(item) {
      this.$router.push({name: 'profile', params: {id: item.id}});
    },
    editAdministrator(item) {
      this.$router.push({name: '/profile', params: {id: item.id}});
    },
    paginationReload(data) {
      this.currentPage = data.currentPage;
      this.perPage = data.perPage;
      this.totalCount = data.totalCount;
      this.loadItems();
    }
  },
  watch: {
    orderColumn: function () {
      this.loadItems();
    },
    orderDirection: function () {
      this.loadItems();
    }
  }
}
</script>
